import {
  Awaitable,
  getEnumValues,
  ISelectOption,
  WithRef,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString, isUndefined } from 'lodash';
import { IBrand } from '../brand';
import { IOrganisation } from '../organisation/organisation';

export type DataPointValue = string | number | boolean | undefined;

export function isDataPointValue(value: unknown): value is DataPointValue {
  return (
    isString(value) || isNumber(value) || isBoolean(value) || isUndefined(value)
  );
}

export interface IMeasureMetadata {
  id: string;
  label: string;
  summary: string;
  formatter?: MeasureFormatter;
  formatterValue?: string | ((value: DataPointValue) => string);
  dataType: MeasureDataType;
  isFilterable: boolean;
  /**
   * Personally Identifiable Information (PII) — data that can identify a
   * specific individual, such as names, emails, phone numbers, or dates of
   * birth. When true, the value will be obfuscated in Marketing Mode.
   */
  isPii?: boolean;
  /**
   * The type of PII obfuscation to apply when `isPii` is true.
   * Determines which fake-data generator is used (e.g. fake name vs fake
   * phone number vs fake date of birth). Defaults to `'name'` if omitted.
   */
  piiType?:
    | 'name'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'id'
    | 'dob'
    | 'address';
  optionResolver?: ReportFilterOptionResolver;
  icon?: MeasureIcon;
  availableAsColumn?: boolean;
  availableAsChart?: boolean;
}

export interface IReportFilterContext {
  organisation: WithRef<IOrganisation>;
  brand: WithRef<IBrand>;
}
export type ReportFilterOption = ISelectOption<DataPointValue> & {
  isDeleted?: boolean;
  selected?: boolean;
};

export type ReportFilterOptionResolver =
  | ((context: IReportFilterContext) => Awaitable<ReportFilterOption[]>)
  | Awaitable<ReportFilterOption[]>;

export enum MeasureDataType {
  None = 'none',
  String = 'string',
  Number = 'number',
  Date = 'date',
  Boolean = 'boolean',
}

export enum MeasureIcon {
  Calendar = 'calendar_today',
  Checkbox = 'check_box',
  CreditCard = 'credit_card',
  Email = 'alternate_email',
  Link = 'link',
  List = 'format_list_bulleted',
  Money = 'attach_money',
  Numbers = 'numbers',
  Percent = 'percent',
  Person = 'person',
  Practice = 'apartment',
  QuestionMark = 'question_mark',
  Referral = 'article_shortcut',
  Schedule = 'schedule',
  Text = 'text_fields',
  Location = 'distance',
  Tag = 'sell',
}

export enum MeasureFormatter {
  Boolean = 'boolean',
  Currency = 'currency',
  Custom = 'custom',
  Day = 'day',
  Hours = 'hours',
  Link = 'link',
  Minutes = 'minutes',
  Month = 'month',
  Number = 'number',
  Percentage = 'percentage',
  Prefix = 'prefix',
  Suffix = 'suffix',
  Text = 'text',
  Time = 'time',
  Timestamp = 'timestamp',
}

export enum MeasureReducer {
  Sum = 'sum',
  Average = 'average',
  Count = 'count',
}

export const ALL_REDUCERS = getEnumValues(MeasureReducer);
